import React, { useState, useEffect } from 'react';
import { FileText, AlertTriangle, RotateCcw, Database, Settings2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ToastAction } from '@/components/ui/toast';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAppSettings } from '@/context/AppSettingsContext';
import { sopSettingsService } from '@/services/sopSettingsService';
import CategoryMigrationTool from './CategoryMigrationTool';

// No props needed

const SOPManagementSection: React.FC = () => {
  const { toast } = useToast();
  const { appSettings, refreshSettings } = useAppSettings();
  const [showFlaggingFeature, setShowFlaggingFeature] = useState(false);
  const [autoGenerateSummaries, setAutoGenerateSummaries] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Load the current settings when the component mounts or when appSettings changes
  useEffect(() => {
    setShowFlaggingFeature(appSettings.showSOPFlaggingFeature);
    setAutoGenerateSummaries(appSettings.autoGenerateSummaries);
  }, [appSettings]);

  const handleToggleFlaggingFeature = async (checked: boolean) => {
    try {
      setIsUpdating(true);
      await sopSettingsService.toggleSOPFlaggingFeature(checked);
      setShowFlaggingFeature(checked);
      await refreshSettings();
      
      toast({
        title: "Setting Updated",
        description: `SOP flagging feature is now ${checked ? 'visible' : 'hidden'}.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error updating flagging feature setting:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update SOP flagging feature setting.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleToggleAutoGenerateSummaries = async (checked: boolean) => {
    try {
      setIsUpdating(true);
      await sopSettingsService.toggleAutoGenerateSummaries(checked);
      setAutoGenerateSummaries(checked);
      await refreshSettings();
      
      toast({
        title: "Setting Updated",
        description: `Auto-generation of summaries is now ${checked ? 'enabled' : 'disabled'}.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error updating auto-generate summaries setting:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update auto-generate summaries setting.",
        variant: "destructive"
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card className="p-6 dark:bg-gray-800">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
        <h2 className="text-xl font-semibold dark:text-white">SOP Management</h2>
      </div>
      
        <div className="space-y-6">
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 text-amber-700 rounded dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
            These operations can take several minutes to complete and affect all SOP data. Use with caution.
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="space-y-2">
              <h3 className="text-lg font-medium dark:text-white">Analyze SOPs Collection</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Check for duplicate entries and analyze the SOPs collection structure.
              </p>
              <Button 
                onClick={async () => {
                  try {
                    toast({
                      title: "Analysis Started",
                      description: "Analyzing SOPs collection. This may take a few minutes...",
                      variant: "default"
                    });
                    
                    // Call the Netlify function endpoint instead of Firebase function directly
                    const response = await fetch('/.netlify/functions/sops/analyze', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      }
                    });
                    
                    if (!response.ok) {
                      throw new Error(`Failed to analyze: ${response.statusText}`);
                    }
                    
                    const data = await response.json();
                    
                    if (data.success) {
                      // Format duplicate titles for display if available
                      let duplicateDetails = '';
                      if (data.result.duplicateTitles && data.result.duplicateTitles.length > 0) {
                        duplicateDetails = `\n\nDuplicate titles include: ${data.result.duplicateTitles.slice(0, 3).map((d: any) => `"${d.title}"`).join(', ')}${data.result.duplicateTitles.length > 3 ? '...' : ''}`;
                      }
                      
                      // Create a more detailed and persistent dialog instead of a toast
                      const dialog = document.createElement('div');
                      dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
                      dialog.innerHTML = `
                        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                          <div class="flex justify-between items-center mb-4">
                            <h2 class="text-xl font-bold dark:text-white">Analysis Complete</h2>
                            <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" id="close-analysis-dialog">
                              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <div class="space-y-4">
                            <div class="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                              <h3 class="font-medium text-blue-800 dark:text-blue-300 mb-2">Summary</h3>
                              <ul class="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                                <li>Total documents: <span class="font-semibold">${data.result.totalDocuments}</span></li>
                                <li>Unique titles: <span class="font-semibold">${data.result.uniqueTitles}</span></li>
                                <li>Potential duplicates: <span class="font-semibold">${data.result.potentialDuplicates}</span></li>
                              </ul>
                            </div>
                            ${data.result.duplicateTitles && data.result.duplicateTitles.length > 0 ? `
                              <div class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                                <h3 class="font-medium text-amber-800 dark:text-amber-300 mb-2">Duplicate Titles</h3>
                                <div class="max-h-40 overflow-y-auto">
                                  <ul class="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                                    ${data.result.duplicateTitles.map((d: { title: string, count: number }) => `<li>"${d.title}" (${d.count} occurrences)</li>`).join('')}
                                  </ul>
                                </div>
                              </div>
                            ` : ''}
                            <div class="mt-6">
                              <p class="text-gray-600 dark:text-gray-400 text-sm">
                                If you see a high number of duplicates, you may want to reset the SOPs collection to match exactly what's in the Google Sheet.
                              </p>
                            </div>
                          </div>
                          <div class="mt-6 flex justify-end">
                            <button class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600" id="confirm-analysis-dialog">
                              OK
                            </button>
                          </div>
                        </div>
                      `;
                      
                      document.body.appendChild(dialog);
                      
                      // Add event listeners to close the dialog
                      document.getElementById('close-analysis-dialog')?.addEventListener('click', () => {
                        document.body.removeChild(dialog);
                      });
                      
                      document.getElementById('confirm-analysis-dialog')?.addEventListener('click', () => {
                        document.body.removeChild(dialog);
                      });
                      
                      // Also show a toast notification
                      toast({
                        title: "Analysis Complete",
                        description: `Found ${data.result.totalDocuments} documents with ${data.result.uniqueTitles} unique titles and ${data.result.potentialDuplicates} potential duplicates.`,
                        variant: "default"
                      });
                    } else {
                      toast({
                        title: "Analysis Failed",
                        description: "Failed to analyze SOPs collection. Check console for details.",
                        variant: "destructive"
                      });
                    }
                  } catch (error) {
                    console.error('Error analyzing SOPs:', error);
                    toast({
                      title: "Analysis Failed",
                      description: "An error occurred while analyzing the SOPs collection.",
                      variant: "destructive"
                    });
                  }
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Analyze Collection
              </Button>
            </div>
            
            <div className="space-y-2">
              <h3 className="text-lg font-medium dark:text-white">Reset SOPs Collection</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Reset the entire SOPs collection to match exactly what's in the Google Sheet.
              </p>
              <Button 
                onClick={() => {
                  // Show a confirmation toast instead of a browser popup
                  toast({
                    title: "Reset SOPs Collection?",
                    description: "This will delete all existing SOPs and recreate them from the Google Sheet. This action cannot be undone.",
                    variant: "destructive",
                    duration: 10000,
                    action: (
                      <ToastAction altText="Confirm Reset" onClick={async () => {
                        try {
                          toast({
                            title: "Reset Started",
                            description: "Resetting SOPs collection. This may take several minutes...",
                            variant: "default"
                          });
                          
                          // Call the Netlify function endpoint instead of Firebase function directly
                          const response = await fetch('/.netlify/functions/sops/reset', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            }
                          });
                          
                          if (!response.ok) {
                            throw new Error(`Failed to reset: ${response.statusText}`);
                          }
                          
                          const data = await response.json();
                          
                          if (data.success) {
                            // Create a more detailed and persistent dialog
                            const dialog = document.createElement('div');
                            dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
                            dialog.innerHTML = `
                              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-2xl w-full mx-4">
                                <div class="flex justify-between items-center mb-4">
                                  <h2 class="text-xl font-bold dark:text-white">Reset Complete</h2>
                                  <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" id="close-reset-dialog">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                                <div class="space-y-4">
                                  <div class="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                    <h3 class="font-medium text-green-800 dark:text-green-300 mb-2">Operation Successful</h3>
                                    <ul class="list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300">
                                      <li>Deleted <span class="font-semibold">${data.result.deletedEntries}</span> documents</li>
                                      <li>Added <span class="font-semibold">${data.result.addedEntries}</span> documents from the sheet</li>
                                      <li>Sheet entries: <span class="font-semibold">${data.result.sheetEntries}</span></li>
                                    </ul>
                                  </div>
                                  <div class="mt-4">
                                    <p class="text-gray-600 dark:text-gray-400">
                                      The SOPs collection has been reset to match exactly what's in the Google Sheet. All duplicates have been removed.
                                    </p>
                                  </div>
                                </div>
                                <div class="mt-6 flex justify-end">
                                  <button class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600" id="confirm-reset-dialog">
                                    OK
                                  </button>
                                </div>
                              </div>
                            `;
                            
                            document.body.appendChild(dialog);
                            
                            // Add event listeners to close the dialog
                            document.getElementById('close-reset-dialog')?.addEventListener('click', () => {
                              document.body.removeChild(dialog);
                            });
                            
                            document.getElementById('confirm-reset-dialog')?.addEventListener('click', () => {
                              document.body.removeChild(dialog);
                            });
                            
                            // Also show a toast notification
                            toast({
                              title: "Reset Complete",
                              description: `Deleted ${data.result.deletedEntries} documents and added ${data.result.addedEntries} documents from the sheet.`,
                              variant: "default"
                            });
                            
                            // Clear the SOP cache
                            const { sopCache } = await import('../../services/sopCache');
                            sopCache.clearCache();
                          } else {
                            toast({
                              title: "Reset Failed",
                              description: "Failed to reset SOPs collection. Check console for details.",
                              variant: "destructive"
                            });
                          }
                        } catch (error) {
                          console.error('Error resetting SOPs:', error);
                          toast({
                            title: "Reset Failed",
                            description: "An error occurred while resetting the SOPs collection.",
                            variant: "destructive"
                          });
                        }
                      }}>
                        Confirm Reset
                      </ToastAction>
                    ),
                  });
                }}
                className="w-full bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Collection
              </Button>
            </div>
          </div>
          
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium dark:text-white mb-2 flex items-center">
              <Database className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              Category Migration
            </h3>
            <CategoryMigrationTool />
          </div>

          {/* Feature Toggles Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium dark:text-white mb-4 flex items-center">
              <Settings2 className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
              Feature Toggles
            </h3>
            <div className="space-y-4">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="flagging-feature-toggle" className="text-base font-medium">
                      SOP Flagging Feature
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Show or hide the "Flag For Review" button and "Show only flagged SOPs" checkbox.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="flagging-feature-toggle"
                      checked={showFlaggingFeature}
                      onCheckedChange={handleToggleFlaggingFeature}
                      disabled={isUpdating}
                    />
                    <Label htmlFor="flagging-feature-toggle">
                      {showFlaggingFeature ? 'Visible' : 'Hidden'}
                    </Label>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-generate-summaries-toggle" className="text-base font-medium">
                      Auto-Generate Summaries
                    </Label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Automatically generate AI summaries for new SOPs when they are added.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="auto-generate-summaries-toggle"
                      checked={autoGenerateSummaries}
                      onCheckedChange={handleToggleAutoGenerateSummaries}
                      disabled={isUpdating}
                    />
                    <Label htmlFor="auto-generate-summaries-toggle">
                      {autoGenerateSummaries ? 'Enabled' : 'Disabled'}
                    </Label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

    </Card>
  );
};

export default SOPManagementSection;
